import algoliasearch from 'algoliasearch/lite'
import { Configure, InstantSearch, useHits } from 'react-instantsearch'
import { styled } from '@/src/stitches.config'
import { MenuListAlphabetical } from '@/src/modules/algolia/MenuListAlphabetical'
import { PaginationControls } from '@/src/modules/algolia/PaginationControls'
import { SearchBoxInput } from '@/src/modules/algolia/SearchBoxInput'
import { HitsList } from '@/src/modules/algolia/HitsList'
import { InsightsMiddleware } from './InsightsMiddleware'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'
import { useBrandSearchContext } from '@/src/modules/algolia/BrandSearchContext'
import BrandSearchRefinements from '@/src/modules/algolia/BrandSearchRefinements'
import { Dispatch, useEffect } from 'react'
import { PG_PLUS_SLUG } from '@/prisma/schemaConstants'

const searchClient = algoliasearch(
    String(process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID),
    String(process.env.NEXT_PUBLIC_ALGOLIA_API_KEY_SEARCH)
)
const indexName = String(process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME_BRANDS)

const BrandSearchWrapper = styled('div', {
    width: 800,
    maxWidth: '100%',
    variants: {
        padding: {
            default: {},
            'py-40': {
                padding: '40px 3%',
            },
        },
    },
    defaultVariants: {
        padding: 'default',
    },
})
const SearchAndFilter = styled('div', {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
})
const SearchWrapper = styled('div', {
    flex: 1,
    position: 'relative',
    '@media (max-width: 390px)': {
        '& svg': {
            display: 'none',
        },
    },
})
const StyledIcon = styled(FontAwesomeIcon, {
    position: 'absolute',
    right: '2.5%',
    top: '25%',
    color: '$primary',
    cursor: 'pointer',
    flex: 0,
    fontSize: '1.65rem',
})

export interface IBrandSearch {
    denomination: number
    isUnbrandedToEgift: boolean
    dispatch?: Dispatch<any>
    padding?: 'default' | 'py-40'
    justifyHitsOnSmallUp?: 'center' | 'space-around' | 'flex-start'
    minHeight?: 'default' | 'brands-page'
}

// used in redemption flows to change brand
export const BrandSearch = ({
    denomination,
    isUnbrandedToEgift,
    padding,
    dispatch,
    justifyHitsOnSmallUp = 'center',
}: IBrandSearch) => {
    const { existingGiftSlug } = useBrandSearchContext()

    // only apply denom filters if user is trying to exchange pg+ for egift
    const denomFilter = isUnbrandedToEgift
        ? ` AND giftya_min_value <= ${denomination} AND giftya_max_value >= ${denomination} AND (has_giftya_fixed_denominations:false OR giftya_fixed_denominations:${denomination})`
        : ''
    const standardFilter =
        `pgc_enabled:true AND NOT slug:visa AND NOT slug:${PG_PLUS_SLUG}` +
        denomFilter
    const filter = existingGiftSlug
        ? standardFilter + ` AND NOT slug:${existingGiftSlug}`
        : standardFilter

    return (
        <BrandSearchWrapper padding={padding} data-testid={'brand-select'}>
            <InstantSearch searchClient={searchClient} indexName={indexName}>
                <Configure filters={filter} clickAnalytics />
                <InsightsMiddleware />
                <BrandSearchAndFilter />
                <MenuListAlphabetical
                    variant={'smaller-solid-blocks'}
                    data-testid={'alphabetical-filter'}
                />
                <BrandSearchRefinements variant={'redemption-flow'} />
                <HitsList
                    imageWidth={'smaller'}
                    justifyHitsOnSmallUp={justifyHitsOnSmallUp}
                    dispatch={dispatch}
                />
                <PaginationControls />
            </InstantSearch>
        </BrandSearchWrapper>
    )
}

export const BrandSearchAndFilter = () => {
    const { setBrandHits } = useBrandSearchContext()
    const { hits } = useHits()
    const { showMagnifyingGlass } = useBrandSearchContext()

    // updates the brand hits for analytic events
    useEffect(() => {
        setBrandHits(hits)
    }, [hits, setBrandHits])

    return (
        <SearchAndFilter className={'brand-search'}>
            <SearchWrapper>
                {showMagnifyingGlass && (
                    <StyledIcon
                        icon={faMagnifyingGlass}
                        data-testid={'search-icon'}
                    />
                )}
                <SearchBoxInput />
            </SearchWrapper>
        </SearchAndFilter>
    )
}
